<template>
    <div class="tabs-container">
        <div class="tabs-row">
            <div class="tab-fixed" :class="{ 'tab-fixed-active': selectedIndex === 0 }" @click="goToListTab">
                <span>Oportunitati</span>
            </div>
            <DxTabs
                v-if="opportunityTabs.length > 0"
                class="tabs-scroll"
                :data-source="opportunityTabs"
                :selected-index="selectedIndex - 1"
                item-template="tabItem"
                :scrolling-enabled="true"
                :show-nav-buttons="true"
                @option-changed="onTabChanged"
            >
                <template #tabItem="{ data, index }">
                    <div class="tab-label">
                        <span>{{ data.text }}</span>
                        <span class="tab-close" @click.stop="closeTab(index)">×</span>
                    </div>
                </template>
            </DxTabs>
        </div>

        <div v-show="selectedIndex === 0" class="tab-content">
            <GridTabOportunitati ref="gridTabList" @select-opportunity="onOpportunitySelected"
                @opportunity-updated="onOpportunityUpdatedInGrid" @opportunity-deleted="onOpportunityDeletedInGrid"
                class="grid-fill" />
        </div>

        <template v-for="(o, i) in openOpportunities" :key="o.opportunityId">
            <div v-show="selectedIndex === i + 1" class="tab-content">
                <GridOportunitatiDetalii :opportunity="o.opportunityData" class="fill"
                    @back="goToListTab" @refresh-data="refreshOpportunity(o.opportunityId)" />
            </div>
        </template>
    </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { DxTabs } from 'devextreme-vue/tabs';
import GridTabOportunitati from './GridTabOportunitati.vue';
import GridOportunitatiDetalii from './GridOportunitatiDetalii.vue';
import CrmSales from '@/services/Comercial/CrmSales';
import DataSource from 'devextreme/data/data_source';
import AppSetting from '@/settings/appSettings';
import notify from 'devextreme/ui/notify';

interface OpenOpportunityTab {
    opportunityId: string;
    opportunityData: Record<string, any>;
}

export default defineComponent({
    components: {
        DxTabs,
        GridTabOportunitati,
        GridOportunitatiDetalii
    },
    data() {
        return {
            selectedIndex: 0,
            openOpportunities: [] as OpenOpportunityTab[],
            opportunityTabs: [] as { text: string, opportunityId: string }[],
        };
    },
    mounted() {
        if (this.$route.query.opportunityId) {
            const id = this.$route.query.opportunityId as string;
            this.fetchOpportunity(id);
        }
    },
    watch: {
        '$route.query.opportunityId': {
            handler(newId) {
                if (newId) {
                    this.fetchOpportunity(newId as string);
                }
            },
        }
    },
    methods: {
        onTabChanged(e: any) {
            if (e.name !== 'selectedIndex') return;
            const internalIndex = e.component.option('selectedIndex');
            if (internalIndex == null || internalIndex < 0) return;
            this.selectedIndex = internalIndex + 1;
        },
        goToListTab() {
            this.selectedIndex = 0;
            (this.$refs.gridTabList as any)?.refreshGrid?.();
        },
        onOpportunitySelected(opportunity: any) {
            this.openOpportunity(opportunity);
        },
        // Oportunitatea a fost editată direct din grid (ex. schimbare status) — dacă are un tab deschis, îl reîmprospătăm
        onOpportunityUpdatedInGrid(id: any) {
            const idStr = String(id);
            if (this.openOpportunities.some(o => o.opportunityId === idStr)) {
                this.refreshOpportunity(idStr);
            }
        },
        // Oportunitatea a fost ștearsă direct din grid — dacă avea un tab deschis, îl închidem
        onOpportunityDeletedInGrid(id: any) {
            const idStr = String(id);
            const idx = this.openOpportunities.findIndex(o => o.opportunityId === idStr);
            if (idx === -1) return;

            const wasActiveTab = this.selectedIndex === idx + 1;
            const activeOpportunityId = wasActiveTab ? null : this.openOpportunities[this.selectedIndex - 1]?.opportunityId;

            this.openOpportunities.splice(idx, 1);
            this.opportunityTabs.splice(idx, 1);

            if (wasActiveTab) {
                notify('Oportunitatea a fost ștearsă — tab-ul a fost închis.', 'info', 3000);
                this.selectedIndex = 0;
            } else {
                const newIndex = activeOpportunityId
                    ? this.openOpportunities.findIndex(o => o.opportunityId === activeOpportunityId) + 1
                    : 0;
                this.selectedIndex = newIndex;
            }
        },
        openOpportunity(opportunity: any) {
            const id = String(opportunity.ID);
            const existingIndex = this.openOpportunities.findIndex(o => o.opportunityId === id);
            if (existingIndex !== -1) {
                this.selectedIndex = existingIndex + 1;
                return;
            }
            this.openOpportunities.push({ opportunityId: id, opportunityData: opportunity });
            this.opportunityTabs = [...this.opportunityTabs, { text: opportunity.Titlu || 'Oportunitate', opportunityId: id }];
            this.selectedIndex = this.opportunityTabs.length;
        },
        closeTab(index: number) {
            const wasActiveTab = this.selectedIndex === index + 1;
            const activeOpportunityId = wasActiveTab ? null : this.openOpportunities[this.selectedIndex - 1]?.opportunityId;

            this.openOpportunities.splice(index, 1);
            this.opportunityTabs = this.opportunityTabs.filter((_, i) => i !== index);

            if (wasActiveTab) {
                // Închizi oportunitatea pe care o vizualizai — te întorci mereu pe gridul de oportunități
                this.selectedIndex = 0;
            } else {
                // Închizi un tab din fundal — rămâi pe oportunitatea curentă, doar recalculezi indexul
                const newIndex = activeOpportunityId
                    ? this.openOpportunities.findIndex(o => o.opportunityId === activeOpportunityId) + 1
                    : 0;
                this.selectedIndex = newIndex;
            }
        },
        loadOpportunity(id: string): Promise<any> {
            const ds = new DataSource({
                store: {
                    type: 'odata',
                    url: AppSetting.API_Url_ComercialOdata + 'CRM_Sales',
                    key: 'ID',
                    keyType: 'Guid',
                    version: 4,
                    beforeSend: (e: any) => {
                        e.headers = {
                            'Content-Type': 'application/json',
                            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
                        };
                        e.params.$expand = 'Account($select=Nume),Contact($select=Nume,Prenume),Campanie($select=Denumire),Valuta($select=Simbol),Sursa($select=Nume),Proiect($select=Cod),Status($select=Nume)';
                    },
                },
                filter: ['ID', '=', id],
            });
            return ds.load();
        },
        fetchOpportunity(id: string) {
            if (!id || id === '') return;
            this.loadOpportunity(id).then((response) => {
                if (response && response.length > 0) {
                    this.openOpportunity(response[0]);
                }
            }).catch(error => {
                console.error("Error fetching opportunity:", error);
            });
        },
        // Reîncarcă o oportunitate deja deschisă (după editare titlu/status în GridOportunitatiDetalii)
        // și actualizează atât datele din tab, cât și eticheta lui — fără să schimbe tab-ul activ.
        refreshOpportunity(id: string) {
            this.loadOpportunity(id).then((response) => {
                if (!response || response.length === 0) return;
                const idx = this.openOpportunities.findIndex(o => o.opportunityId === id);
                if (idx === -1) return;
                this.openOpportunities[idx].opportunityData = response[0];
                this.opportunityTabs[idx].text = response[0].Titlu || 'Oportunitate';
            }).catch(error => {
                console.error("Error refreshing opportunity:", error);
            });
        }
    }

});
</script>
<style scoped>
.tabs-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tabs-row {
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
}

.tab-fixed {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 16px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    border-bottom: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
}

.tab-fixed:hover {
    color: rgba(0, 0, 0, 0.87);
}

.tab-fixed-active {
    color: #ff2c21;
    border-bottom-color: #ff2c21;
    font-weight: 600;
}

.tabs-scroll {
    flex: 1;
    min-width: 0;
}

.tab-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.grid-fill {
    flex: 1;
    min-height: 0;
}

.fill {
    flex: 1;
    min-height: 0;
    width: 100%;
}

.tab-label {
    display: flex;
    align-items: center;
    gap: 6px;
}

.tab-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font-size: 14px;
    line-height: 1;
    color: #94a3b8;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.tab-close:hover {
    background: #fee2e2;
    color: #ef4444;
}
</style>
